import { useCallback, useMemo, useState } from "react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

export type SortDirection = "asc" | "desc";

/** Page-navigation request sent to a list endpoint alongside the query. */
export interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

/**
 * The resolved (debounced) query a list endpoint consumes. This is what goes
 * into the TanStack Query key, so it must be stable across renders.
 */
export interface DataGridQuery {
  search: string;
  status: string;
  sort: string;
  sortDir: SortDirection;
}

export const DEFAULT_LIMIT = 50;

/**
 * Serialize a grid query + page request into the query string every paginated
 * list endpoint expects (`limit`, `cursor`, `direction`, `search`, `status`,
 * `sort`, `sortDir`). Used by all entity list hooks so the wire format stays
 * identical across grids.
 */
export function buildListParams(
  query: DataGridQuery,
  pageRequest: PageRequest,
  limit: number = DEFAULT_LIMIT,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (pageRequest.cursor) {
    params.set("cursor", pageRequest.cursor);
    params.set("direction", pageRequest.direction);
  }
  if (query.search) params.set("search", query.search);
  if (query.status) params.set("status", query.status);
  params.set("sort", query.sort);
  params.set("sortDir", query.sortDir);
  return params;
}

interface UseDataGridOptions {
  /** Column key the grid sorts by initially (must match a server `sort` value). */
  defaultSort: string;
  /** Initial direction; defaults to "desc" (newest-first for date columns). */
  defaultSortDir?: SortDirection;
  /** Search debounce in ms. */
  searchDelayMs?: number;
}

export interface DataGrid {
  /** Un-debounced value bound to the `SearchInput`. */
  searchInput: string;
  setSearch: (value: string) => void;
  status: string;
  setStatus: (value: string) => void;
  sort: string;
  sortDir: SortDirection;
  /** Toggle/select the sort column — same key flips direction, new key sorts asc. */
  toggleSort: (key: string) => void;
  /** 1-based page number, for display only (cursor pagination has no offset). */
  page: number;
  pageRequest: PageRequest;
  /** Advance using the `nextCursor` from the latest response (no-op if null). */
  goNext: (nextCursor: string | null) => void;
  /** Go back using the `prevCursor` from the latest response (no-op if null). */
  goPrev: (prevCursor: string | null) => void;
  /** Debounced query object — feed straight into the entity list hook + query key. */
  query: DataGridQuery;
  /**
   * Return to the first page and drop the cursor. The hook calls this itself for
   * the filters it owns (search, status, sort); expose it for a grid that adds a
   * filter of its own OUTSIDE the hook — a cursor from the previous filter points
   * into a different result set, so paging on without resetting lands on the
   * wrong rows or an empty page.
   */
  resetPage: () => void;
}

/**
 * Owns every piece of list-grid UI state — search (debounced), status filter,
 * sort column/direction, and cursor pagination — and resets to the first page
 * whenever a filter or sort changes. Pair it with a server endpoint built on
 * `keysetPaginate` and the sortable `DataTable`. See `.claude/rules/frontend.md`.
 */
export function useDataGrid(options: UseDataGridOptions): DataGrid {
  const { defaultSort, defaultSortDir = "desc", searchDelayMs = 300 } = options;

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, searchDelayMs);
  const [status, setStatusValue] = useState("");
  const [sortState, setSortState] = useState<{ key: string; dir: SortDirection }>({
    key: defaultSort,
    dir: defaultSortDir,
  });
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<PageRequest>({ cursor: null, direction: "next" });

  const resetToFirstPage = useCallback(() => {
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }, []);

  const setSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const setStatus = useCallback(
    (value: string) => {
      setStatusValue(value);
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const toggleSort = useCallback(
    (key: string) => {
      setSortState((prev) =>
        prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
      );
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const goNext = useCallback((nextCursor: string | null) => {
    if (!nextCursor) return;
    setPage((p) => p + 1);
    setPageRequest({ cursor: nextCursor, direction: "next" });
  }, []);

  const goPrev = useCallback((prevCursor: string | null) => {
    if (!prevCursor) return;
    setPage((p) => Math.max(1, p - 1));
    setPageRequest({ cursor: prevCursor, direction: "prev" });
  }, []);

  const query = useMemo<DataGridQuery>(
    () => ({ search: debouncedSearch, status, sort: sortState.key, sortDir: sortState.dir }),
    [debouncedSearch, status, sortState],
  );

  return {
    searchInput,
    setSearch,
    status,
    setStatus,
    sort: sortState.key,
    sortDir: sortState.dir,
    toggleSort,
    page,
    pageRequest,
    goNext,
    goPrev,
    query,
    resetPage: resetToFirstPage,
  };
}
